// Copyright 2020 Sorama B.V.

import { bindable, computedFrom, containerless, customElement, Disposable } from "aurelia-framework";
import { Router } from "aurelia-router";
import { Quad } from "datatypes/Generic/Quad";
import {
    CanvasMediaObjectLinkDTO,
    LinkedMediaObjectDTO,
    TwoDimensionalLinkedMediaObjectDTO,
    LinkedMediaObjectType,
    TextAlignments,
    TextBoxDTO,
    FileDTO,
    SnapshotDTO,
    VideoDTO,
} from "datatypes/SoramaWebApi";
import { CanvasDTO, TargetAndSourceRegionLinkProperties } from "datatypes/SoramaWebApi";
import { ResizeListener } from "libraries/Layouting/ResizeListener";
import { ICanvasMediaObjectLinkRepository } from "repositories/CanvasMediaObjectLinkRepository";
import { ICanvasRepository } from "repositories/CanvasRepository";
import { ILinkedMediaObjectRepository } from "repositories/LinkedMediaObjectRepository";
import { IComposableCanvasController } from "./ComposableCanvasController";
import { ComposableFileMediaObject } from "./elements/ComposableFileMediaObject";
import { ComposableSnapshotMediaObject } from "./elements/ComposableSnapshotMediaObject";
import { ComposableTextMediaObject } from "./elements/ComposableTextMediaObject";
import { ComposableVideoMediaObject } from "./elements/ComposableVideoMediaObject";
import { ComposableImageMediaObject } from "./elements/ComposableImageMediaObject";

export interface Size {
    width: number;
    height: number;
}

export interface BoundingBox extends Size {
    x: number;
    y: number;
}

export interface CanvasLink {
    link: CanvasMediaObjectLinkDTO;
    mediaObject: LinkedMediaObjectDTO;
}

export interface IComposableParent {
    getNextLayerUp(currentLayer: number): number;
    getNextLayerDown(currentLayer: number): number;
    delete(child: IComposableChild): Promise<void>;
    readonly readOnly: boolean;
}

export interface IComposableChild {
    readonly layer: number;
    readonly boundingBox: BoundingBox;
    readonly linkId: string;
    readonly mediaObjectType: LinkedMediaObjectType;
    readonly mediaObjectId: string;
    setComposableAreaSize(area: BoundingBox): void;
}

export interface IComposableCanvasControls {
    addMediaObject(mediaObjectId: string): Promise<void>;
}

@containerless()
@customElement("composable-canvas")
export class ComposableCanvas implements IComposableParent, IComposableCanvasControls {
    /**
     * The id of the canvas element to show/manipulate
     */
    @bindable()
    public canvasId: string;

    /**
     * Indicates that the element cannot be manipulated.
     */
    @bindable()
    public readOnly: boolean;

    /**
     * Indicates that the control should display an option to toggle the readonly field
     */
    @bindable()
    public canEdit: boolean;

    @bindable()
    public canPresent: boolean;

    //#region HTML (ref) elements
    protected containerElement: HTMLDivElement;

    //#endregion

    //#region HTML UI properties/fields

    @computedFrom("containerElement.clientWidth", "containerElement.clientHeight", "canvas")
    protected get controlScale(): number {
        return this.canvas
            ? Math.min(
                  this.containerElement.clientWidth / this.canvas.size.width,
                  this.containerElement.clientHeight / this.canvas.size.height
              )
            : 1;
    }

    @computedFrom("canvas", "controlScale")
    protected get canvasElementWidth(): number {
        if (!this.canvas) {
            return 0;
        }

        return this.canvas.size.width * this.controlScale;
    }

    @computedFrom("canvasElementWidth", "containerElement.clientWidth")
    protected get marginLeft(): number {
        return (this.containerElement.clientWidth - this.canvasElementWidth) / 2;
    }

    @computedFrom("canvas", "controlScale")
    protected get canvasElementHeight(): number {
        if (!this.canvas) {
            return 0;
        }

        return this.canvas.size.height * this.controlScale;
    }

    protected loading: boolean;

    //#endregion

    //#region privates
    private canvas: CanvasDTO;
    private canvasLinks: CanvasLink[];

    private mediaObjects: IComposableChild[];

    private canvasControllerDisposable: Disposable;

    //#endregion

    constructor(
        private readonly controller: IComposableCanvasController,
        private readonly canvasRepository: ICanvasRepository,
        private readonly linkedMediaObjectRepository: ILinkedMediaObjectRepository,
        private readonly canvasMediaObjectLinkRepository: ICanvasMediaObjectLinkRepository,
        private readonly resizeListener: ResizeListener,
        private readonly router: Router
    ) {}

    public bind() {
        // At this point binding has been evaluated, but aurelia does not call change handlers for this
        if (this.canvasId) {
            this.canvasIdChanged(this.canvasId);
        }
    }

    public attached() {
        // At this point the view should be rendered and we should have received a reference to the container element
        if (!this.containerElement) {
            console.error("ComposableCanvas: Unable to render view, no HTML component has been attached.");
            return;
        }

        this.resizeListener.elementResizeEvent(this.containerElement, this.onResize);
    }

    public detached() {
        if (this.canvasControllerDisposable) {
            this.canvasControllerDisposable.dispose();
            delete this.canvasControllerDisposable;
        }
    }

    // Arrow notation to capture 'this'
    private onResize = () => {
        if (this.mediaObjects && this.containerElement) {
            const bbox = this.getBoundingBox();
            this.mediaObjects.forEach((x) => x.setComposableAreaSize(bbox));
        }
    };

    //#region IComposableCanvasControls

    public addMediaObject(mediaObjectOrId: string | LinkedMediaObjectDTO): Promise<void> {
        const mediaObjectPromise =
            typeof mediaObjectOrId === "string"
                ? this.linkedMediaObjectRepository.get(<string>mediaObjectOrId)
                : Promise.resolve(<LinkedMediaObjectDTO>mediaObjectOrId);

        return mediaObjectPromise.then((mediaObject) => {
            let targetRegion: Quad;
            const dimension = (<TwoDimensionalLinkedMediaObjectDTO>mediaObject).dimension;
            if (dimension) {
                targetRegion = Quad.fitQuadInArea(dimension.width / dimension.height, {
                    width: this.canvasElementWidth,
                    height: this.canvasElementHeight,
                });
                targetRegion = Quad.scale(targetRegion, 1 / this.canvasElementWidth, 1 / this.canvasElementHeight);
            } else {
                targetRegion = {
                    bottomLeft: { x: 0.25, y: 0.75 },
                    bottomRight: { x: 0.75, y: 0.75 },
                    topRight: { x: 0.75, y: 0.25 },
                    topLeft: { x: 0.25, y: 0.25 },
                };
            }

            // Add media object to the canvas
            return this.canvasRepository
                .createMediaObjectLink(this.canvasId, mediaObject.id, <TargetAndSourceRegionLinkProperties>{
                    targetRegion: targetRegion,
                    layer:
                        this.mediaObjects && this.mediaObjects.length > 0
                            ? Math.max(...this.mediaObjects.map((x) => x.layer)) + 1
                            : 0,
                })
                .then((link) => {
                    const bbox = this.getBoundingBox();
                    this.mediaObjects.push(this.toComposableMediaObject(mediaObject, link, bbox));
                });
        });
    }

    public addTextBox(): Promise<void> {
        return this.linkedMediaObjectRepository
            .createTextbox({
                text: "TextBox",
                background: "#00000000" as unknown as number,
                color: "#FFFFFFFF" as unknown as number,
                fontSize: 6,
                textAlignment: TextAlignments.Left,
            })
            .then((x) => {
                return this.addMediaObject(x);
            });
    }

    public present(): void {
        this.containerElement.requestFullscreen();
    }

    public onPresentationClick = (event: MouseEvent) => {
        if (document.fullscreenElement !== this.containerElement) {
            return;
        }

        // Check proper button
        if (event.button == 0 || event.button == 4) {
            this.next();
        } else if (event.button == 3 || event.button == 2) {
            this.previous();
        }

        event.stopImmediatePropagation();
        event.stopPropagation();
        event.preventDefault();
        event.cancelBubble = true;
    };

    private next(): Promise<void> {
        return this.canvasRepository.getNextPage(this.canvasId).then((canvas) => {
            if (!canvas) {
                // End of presentation
                return;
            }
            this.canvas = canvas;
            this.canvasId = this.canvas.id;
        });
    }

    private previous(): Promise<void> {
        return this.canvasRepository.getPreviousPage(this.canvasId).then((canvas) => {
            if (!canvas) {
                // End of presentation
                return;
            }
            this.canvas = canvas;
            this.canvasId = this.canvas.id;
        });
    }

    //#endregion

    //#region IComposableParent members

    public getNextLayerUp(currentLayer: number): number {
        // We want to determine the next layer that will make a change
        let childrenAtCurrentLevel = 0;
        let nextLayer = Number.MAX_SAFE_INTEGER;

        for (const child of this.mediaObjects) {
            if (child.layer > currentLayer && child.layer < nextLayer) {
                nextLayer = child.layer;
            }

            if (child.layer == currentLayer) {
                childrenAtCurrentLevel++;
            }
        }

        if (nextLayer == Number.MAX_SAFE_INTEGER) {
            return childrenAtCurrentLevel == 1 ? currentLayer : currentLayer + 1;
        } else {
            return nextLayer;
        }
    }

    public getNextLayerDown(currentLayer: number): number {
        let candidateLayer = Number.MIN_SAFE_INTEGER;
        let minLayer = Number.MAX_SAFE_INTEGER;
        for (const child of this.mediaObjects) {
            if (child.layer < currentLayer && child.layer > candidateLayer) {
                candidateLayer = child.layer;
            }

            minLayer = Math.min(minLayer, child.layer);
        }

        if (currentLayer == minLayer && currentLayer > 0) {
            candidateLayer = currentLayer - 1;
        }

        return candidateLayer == Number.MIN_SAFE_INTEGER ? currentLayer : candidateLayer;
    }

    public delete(child: IComposableChild): Promise<void> {
        return this.canvasMediaObjectLinkRepository.delete(child.linkId).then(() => {
            const index = this.mediaObjects.findIndex((x) => x === child);
            if (index !== -1) {
                this.mediaObjects.splice(index, 1);
            }

            if (child.mediaObjectType == LinkedMediaObjectType.TextBox) {
                // Also delete the textbox itself
                return this.linkedMediaObjectRepository.delete(child.mediaObjectId);
            }
        });
    }

    //#endregion

    //#region Change handlers

    protected canvasIdChanged(newCanvasId: string) {
        if (newCanvasId) {
            this.loading = true;

            const canvasPromise =
                !this.canvas || this.canvas.id !== newCanvasId
                    ? this.canvasRepository.get(newCanvasId)
                    : Promise.resolve(this.canvas);

            Promise.all([
                canvasPromise.then((canvas) => {
                    this.canvas = canvas;
                }),
                this.canvasRepository.getMediaObjectLinks(newCanvasId).then((links) => {
                    this.canvasLinks = links.map((link) => ({ link: link, mediaObject: undefined }));
                    const promises = this.canvasLinks.map((x) => {
                        return this.linkedMediaObjectRepository.get(x.link.mediaObjectId).then((mediaObject) => {
                            x.mediaObject = mediaObject;
                        });
                    });

                    return Promise.all(promises);
                }),
            ])
                .then(() => {
                    const bbox = this.getBoundingBox();

                    this.mediaObjects = this.canvasLinks.map((x) =>
                        this.toComposableMediaObject(x.mediaObject, x.link, bbox)
                    );

                    if (this.canvasControllerDisposable) {
                        this.canvasControllerDisposable.dispose();
                    }
                    this.canvasControllerDisposable = this.controller.registerCanvas(newCanvasId, this);

                    if (this.canvasControllerDisposable) {
                        this.canvasControllerDisposable.dispose();
                    }
                    this.canvasControllerDisposable = this.controller.registerCanvas(newCanvasId, this);
                })
                .catch((e) => {
                    console.error("ComposableCanvas: Unable to load canvas");
                    console.error(e);
                })
                .then(() => {
                    this.loading = false;
                });
        } else {
            delete this.canvas;
        }
    }

    //#endregion

    private getBoundingBox(): BoundingBox {
        const bbox = this.containerElement.getBoundingClientRect();
        bbox.width = this.canvasElementWidth;
        bbox.height = this.canvasElementHeight;
        return bbox;
    }

    private toComposableMediaObject(
        mediaObject: LinkedMediaObjectDTO,
        link: CanvasMediaObjectLinkDTO,
        bbox: BoundingBox
    ) {
        switch (mediaObject.type) {
            case LinkedMediaObjectType.Image:
                return new ComposableImageMediaObject(
                    this.canvasMediaObjectLinkRepository,
                    this,
                    link,
                    bbox,
                    this.linkedMediaObjectRepository.getData(mediaObject.id).then((data) => URL.createObjectURL(data)),
                    mediaObject as TwoDimensionalLinkedMediaObjectDTO
                );

            case LinkedMediaObjectType.Snapshot:
                return new ComposableSnapshotMediaObject(
                    this.canvasMediaObjectLinkRepository,
                    this,
                    link,
                    bbox,
                    this.linkedMediaObjectRepository.getData(mediaObject.id).then((data) => URL.createObjectURL(data)),
                    mediaObject as SnapshotDTO,
                    this.router
                );

            case LinkedMediaObjectType.TextBox:
                return new ComposableTextMediaObject(
                    this.linkedMediaObjectRepository,
                    this.canvasMediaObjectLinkRepository,
                    this,
                    link,
                    bbox,
                    mediaObject as TextBoxDTO
                );

            case LinkedMediaObjectType.Video:
                return new ComposableVideoMediaObject(
                    this.canvasMediaObjectLinkRepository,
                    this,
                    link,
                    bbox,
                    this.linkedMediaObjectRepository.getData(mediaObject.id).then((data) => URL.createObjectURL(data)),
                    mediaObject as VideoDTO
                );

            case LinkedMediaObjectType.File:
                return new ComposableFileMediaObject(
                    this.canvasMediaObjectLinkRepository,
                    this,
                    link,
                    bbox,
                    mediaObject as FileDTO,
                    this.linkedMediaObjectRepository
                );

            default:
                throw new Error("Unsupported media object type");
        }
    }
}
